import { useRef, useState } from "react";
import { uploadMedia } from "@/services/mediaUpload";

type Props = {
  label: string;
  hint: string;
  ratio: string; // CSS aspect-ratio, ej. "16 / 9"
  value: string;
  onChange: (url: string) => void;
};

/** Sube una portada a Cloudflare y la muestra con la proporción exacta. */
export default function ImageUploader({ label, hint, ratio, value, onChange }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File) => {
    setError(null);
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) return setError("Usa una imagen JPG, PNG o WebP.");
    if (file.size > 8 * 1024 * 1024) return setError("La imagen pesa más de 8 MB.");
    setBusy(true);
    setPct(0);
    try {
      const r = await uploadMedia(file, "thumb", setPct, new AbortController().signal);
      onChange(r.url);
    } catch (err) {
      setError(err instanceof Error ? err.message : "No se pudo subir la imagen.");
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  return (
    <div>
      <div className="text-xs font-semibold text-foreground-600">{label}</div>
      <div className="text-[11px] text-foreground-500 mb-1.5">{hint}</div>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); }} />
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={busy}
        className="relative w-full rounded-xl border border-dashed border-primary-400 bg-primary-500/5 overflow-hidden cursor-pointer hover:bg-primary-500/10"
        style={{ aspectRatio: ratio }}
      >
        {value ? <img src={value} alt="" className="absolute inset-0 w-full h-full object-cover" /> : null}
        <span className={`absolute inset-0 flex flex-col items-center justify-center gap-1 text-xs font-semibold ${value ? "bg-black/35 text-white opacity-0 hover:opacity-100 transition-opacity" : "text-primary-700"}`}>
          <i className="ri-image-add-line text-xl"></i>
          {busy ? `Subiendo… ${pct}%` : value ? "Cambiar imagen" : "Subir imagen"}
        </span>
      </button>
      {value && !busy ? (
        <button type="button" onClick={() => onChange("")} className="mt-1 text-[11px] text-accent-700 font-semibold cursor-pointer">Quitar</button>
      ) : null}
      {error ? <p className="mt-1 text-[11px] text-accent-700">{error}</p> : null}
    </div>
  );
}
